import { Alert, Autocomplete, Button, OutlinedInput, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import { assetService } from "../../../Core/Api/services";
import { ChangeStatusRequest } from "../../../Core/Api/Types/change-status-request";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { GetObjectStatusLabel } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";

export type ModalChangeStatusElementProps = {
  asset: DbAssetObjectInfos;
};

export type ModalChangeStatusProps = CommonModalProps<ModalChangeStatusElementProps>;

const ModalChangeStatus = React.forwardRef<any, ModalChangeStatusProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, ...modalProps } = props;
  const [statuses, setStatuses] = React.useState<ObjectStatus[]>([]);
  const [chosenStatus, setChosenStatus] = React.useState<ObjectStatus>();
  const [message, setMessage] = React.useState<string>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const customSnackbar = useCustomSnackbar();
  const { asset } = modalProps;

  React.useEffect(() => {
    assetService.GetStatusTransitions(asset.id).then((response) => {
      let statuses = response.transitions?.find((x) => x.inputStatus === asset.status)?.outputStatuses;
      if (statuses !== null && statuses !== undefined) {
        setStatuses(statuses);
        setIsLoading(false);
      }
    });
  }, []);

  const handleConfirmClick = () => {
    let request = new ChangeStatusRequest({ status: chosenStatus, message: message, objectId: asset.id });
    assetService.ChangeStatus(request).then(() => {
      customSnackbar(t("alert:success.statusChanged", { status: chosenStatus }), "success");
    });
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.changeStatusOfAssetID", { id: asset.id, name: asset.name })}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle2">{t("button:changeStatus")}</Typography>
        <Stack spacing={0}>
          <Typography variant="h6" component="span" color="text.secondary">
            {t("modal:modalChangesStatus.changeStatus.selectStatusText")}
          </Typography>
          <Autocomplete
            onChange={(e, value) => {
              value !== null ? setChosenStatus(value) : setChosenStatus(undefined);
            }}
            getOptionLabel={(x: ObjectStatus) => GetObjectStatusLabel(x)}
            options={statuses}
            renderInput={(params) => <TextField {...params} placeholder={t("common:selectAnOption")} />}
          />
        </Stack>

        <Stack spacing={2} mb={50}>
          <Typography variant="h6" component="span" color="text.secondary">
            {t("modal:modalChangesStatus.changeStatus.leaveMessageText")}
          </Typography>
          <OutlinedInput placeholder={t("common:message")} onChange={(e) => setMessage(e.target.value)} />
        </Stack>

        {!isLoading && statuses?.length === 0 && (
          <Alert sx={{ mt: 2 }} severity="warning">
            {t("alert:warning.noMatchingStatusTrasition")}
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" mt={4}>
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            disabled={chosenStatus === undefined || message === undefined || message.length < 5}
            onClick={handleConfirmClick}
            variant="contained"
            color="primary"
          >
            {t("button:changeStatus")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
});
export default ModalChangeStatus;
